import { LISTING_TYPE } from '../constants/listingAttributes';
import { LANGUAGE_CODES, LOCALE_TO_LANG, getLocaleFromUrl } from '../constants/locales';
import { findOptionsForSelectFilter } from '../util/search';

import enUSMessages from '../translations/en-US.json';
import frFRMessages from '../translations/fr-FR.json';
import deDEMessages from '../translations/de-DE.json';
import itITMessages from '../translations/it-IT.json';

/* eslint-disable import/no-anonymous-default-export */
export default {
  generalMessages: {
    "english": enUSMessages,
    "french": frFRMessages,
    "german": deDEMessages,
    "italian": itITMessages,
  },

  saveTitleMessages(lang, messages) {
    this.generalMessages[lang] = Object.entries(messages).reduce((generalMsgs, entry) => {
      const [transKey, transVal] = entry;

      if (transKey.includes('General.')) {
        generalMsgs[transKey] = transVal;
      }

      return generalMsgs;
    }, {});
  },

  getDataForTitle(listing) {
    const { attributes, author } = listing || {};
    const { publicData } = attributes || {};
    const { discipline, type, multiLangAddressParts } = publicData || {};
    const authorName = author?.attributes?.profile?.displayName;

    return { discipline, type, multiLangAddressParts, authorName };
  },

  createTitle(intl, listing, forcedLangCode = null, currentPathname = null) {
    const { discipline, type, multiLangAddressParts, authorName } = this.getDataForTitle(listing);
    
    // Use getLocaleFromUrl with the current pathname to get the actual locale during SSR
    const actualLocale = getLocaleFromUrl(currentPathname) || intl.locale;
    const lang = forcedLangCode || LOCALE_TO_LANG[actualLocale];

    let title = '';

    // Add discipline
    if (discipline) {
      const msgId = `General.${discipline}`;
      const translatedDiscipline =
        this.generalMessages[lang]?.[msgId] || intl.formatMessage({ id: msgId });
      title += translatedDiscipline.toLowerCase();
    }

    // Add type if it's a free listing
    if (type === LISTING_TYPE.FREE) {
      const msgId = `General.freeSharing`;
      const translatedType =
        this.generalMessages[lang]?.[msgId] || intl.formatMessage({ id: msgId });
      title += ' ' + translatedType.toLowerCase();
    }

    // Get current language from actual locale
    const currentLanguage = actualLocale.split('-')[0]; // 'en' from 'en_US'

    const cityName = multiLangAddressParts?.[forcedLangCode || this.getLanguageKey(currentLanguage)]?.city;

    // Add location with translated "in"
    if (cityName) {
      const inText = this.generalMessages[lang]?.['General.in'] ||
        intl.formatMessage({ id: 'General.in' });
      title += ` ${inText} ${cityName}`;
    }

    // Add author with translated "with"
    if (authorName) {
      const withText = this.generalMessages[lang]?.['General.with'] ||
        intl.formatMessage({ id: 'General.with' });
      title += ` ${withText} ${authorName}`;
    }

    return title.replace(/\s\s+/g, ' ').trim();
  },

  // Helper function to get language key
  getLanguageKey(lang) {
    switch (lang) {
      case 'de':
        return 'german';
      case 'it':
        return 'italian';
      case 'fr':
        return 'french';
      default:
        return 'english';
    }
  },

  createMultiLangTitles(intl, listing, currentPathname = null) {
    return (LANGUAGE_CODES).reduce((titles, lang) => {
      return { ...titles, [LOCALE_TO_LANG[lang]]: this.createTitle(intl, listing, LOCALE_TO_LANG[lang], currentPathname) };
    }, {});
  },

  createMultiLangActivity(activity, config) {
    const activities = findOptionsForSelectFilter('activity', config.search.defaultFilters);

    return LANGUAGE_CODES.reduce((translated, lang) => {
      const labelId = activities?.find(a => a.key === activity)?.label;

      if (!labelId) return translated;

      return {
        ...translated,
        [LOCALE_TO_LANG[lang]]: this.generalMessages[lang]?.[labelId] || activity,
      };
    }, {});
  },
};